import type { LucideIcon } from "lucide-react";
import { AlertCircle, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PageShellProps {
  children: ReactNode;
  className?: string;
}

export function PageShell({ children, className }: PageShellProps) {
  return (
    <div className={cn("app-page", className)}>
      {children}
    </div>
  );
}

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  icon: Icon,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("app-page-header", className)}>
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-700">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              {eyebrow}
            </div>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            {title}
          </h1>
          {description && (
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          {actions}
        </div>
      )}
    </header>
  );
}

interface PageSectionProps {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function PageSection({
  children,
  title,
  description,
  actions,
  className,
  contentClassName,
}: PageSectionProps) {
  const hasHeader = title || description || actions;

  return (
    <section className={cn("app-section", className)}>
      {hasHeader && (
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            {title && <h2 className="text-base font-semibold text-slate-950 sm:text-lg">{title}</h2>}
            {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn("p-4 sm:p-6", contentClassName)}>{children}</div>
    </section>
  );
}

export function FilterToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:flex-row sm:items-center sm:p-4",
        className,
      )}
      role="search"
    >
      {children}
    </div>
  );
}

interface MetricCardProps {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  helper?: ReactNode;
  tone?: "neutral" | "info" | "success" | "warning" | "danger";
  className?: string;
}

const metricTones = {
  neutral: "border-slate-200 bg-white text-slate-700",
  info: "border-blue-200 bg-blue-50/60 text-blue-700",
  success: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
  warning: "border-amber-200 bg-amber-50/60 text-amber-800",
  danger: "border-red-200 bg-red-50/60 text-red-700",
};

export function MetricCard({
  label,
  value,
  icon: Icon,
  helper,
  tone = "neutral",
  className,
}: MetricCardProps) {
  return (
    <div className={cn("rounded-xl border p-4 shadow-sm", metricTones[tone], className)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums text-slate-950 sm:text-3xl">{value}</div>
      {helper && <div className="mt-1 text-xs opacity-80">{helper}</div>}
    </div>
  );
}

export function PageLoading({ label = "Carregando dados..." }: { label?: string }) {
  return (
    <div
      className="flex min-h-56 flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white text-slate-500"
      aria-busy="true"
      aria-live="polite"
    >
      <Loader2 className="h-7 w-7 animate-spin text-indigo-600" aria-hidden="true" />
      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}

interface PageErrorProps {
  title?: string;
  description: string;
  onRetry?: () => void;
}

export function PageError({
  title = "Não foi possível carregar esta página",
  description,
  onRetry,
}: PageErrorProps) {
  return (
    <div
      className="flex min-h-56 flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50/70 p-6 text-center"
      role="alert"
    >
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white text-red-600 shadow-sm">
        <AlertCircle className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-semibold text-red-950">{title}</h2>
      <p className="mt-1 max-w-md text-sm text-red-800">{description}</p>
      {onRetry && (
        <Button type="button" variant="outline" className="mt-4 bg-white" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}
